import { useCallback, useRef, useState } from "react";

interface CameraState {
  isActive: boolean;
  error: string | null;
  loading: boolean;
}

export const useCamera = () => {
  const [state, setState] = useState<CameraState>({
    isActive: false,
    error: null,
    loading: false,
  });
  const streamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const isStartingRef = useRef(false);

  const start = useCallback(async (videoElement: HTMLVideoElement) => {
    // Prevent multiple simultaneous start calls
    if (isStartingRef.current) {
      console.log("Camera already starting, skipping");
      return;
    }

    // If stream already exists and is active, just reconnect to new element
    if (streamRef.current && streamRef.current.active) {
      console.log("Reusing existing active stream");
      videoElement.srcObject = streamRef.current;
      videoRef.current = videoElement;
      await videoElement.play();
      setState({ isActive: true, error: null, loading: false });
      return;
    }

    isStartingRef.current = true;
    videoRef.current = videoElement;
    setState((s) => ({ ...s, loading: true, error: null }));

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });

      // Check if component is still mounted and element matches
      if (videoRef.current === videoElement) {
        videoElement.srcObject = stream;
        streamRef.current = stream;

        // Wait for video to be ready
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error("Video metadata load timeout"));
          }, 10000); // 10s timeout

          videoElement.onloadedmetadata = async () => {
            clearTimeout(timeout);
            try {
              await videoElement.play();
              resolve();
            } catch (playErr) {
              reject(playErr);
            }
          };
        });

        setState({ isActive: true, error: null, loading: false });
      } else {
        // Component unmounted or element changed, clean up stream
        stream.getTracks().forEach((track) => track.stop());
      }
    } catch (err) {
      console.error("Camera start error:", err);
      // `getUserMedia` distingue ses refus par le nom de l'erreur levée.
      const errorName = err instanceof Error ? err.name : "";
      let errorMessage = "Impossible d'accéder à la caméra.";

      if (
        errorName === "NotAllowedError" ||
        errorName === "PermissionDeniedError"
      ) {
        errorMessage =
          "L'accès à la caméra a été refusé. Veuillez autoriser l'accès dans les paramètres de votre navigateur pour utiliser cette application.";
      } else if (
        errorName === "NotFoundError" ||
        errorName === "DevicesNotFoundError"
      ) {
        errorMessage = "Aucune caméra n'a été trouvée sur cet appareil.";
      } else if (
        errorName === "NotReadableError" ||
        errorName === "TrackStartError"
      ) {
        errorMessage = "La caméra est déjà utilisée par une autre application.";
      }

      setState({ isActive: false, error: errorMessage, loading: false });
    } finally {
      isStartingRef.current = false;
    }
  }, []);

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        if (track.readyState === "live") {
          track.stop();
        }
      });
      streamRef.current = null;
    }
    if (videoRef.current) {
      if (videoRef.current.srcObject) {
        videoRef.current.srcObject = null;
      }
      videoRef.current = null;
    }
    isStartingRef.current = false;
    setState({ isActive: false, error: null, loading: false });
  }, []);

  const capture = useCallback(async () => {
    if (!videoRef.current || !streamRef.current) {
      throw new Error("Camera not active");
    }

    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    const thumbCanvas = document.createElement("canvas");

    // Set dimensions to match video stream
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw full size
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not get canvas context");
    ctx.drawImage(video, 0, 0);

    // Create thumbnail (max 200px)
    const ratio = video.videoWidth / video.videoHeight;
    thumbCanvas.width = 200;
    thumbCanvas.height = 200 / ratio;
    const thumbCtx = thumbCanvas.getContext("2d");
    if (!thumbCtx) throw new Error("Could not get thumbnail context");
    thumbCtx.drawImage(video, 0, 0, thumbCanvas.width, thumbCanvas.height);

    const [photoBlob, thumbnailBlob] = await Promise.all([
      new Promise<Blob>((resolve) =>
        canvas.toBlob((b) => resolve(b!), "image/jpeg", 0.8)
      ),
      new Promise<Blob>((resolve) =>
        thumbCanvas.toBlob((b) => resolve(b!), "image/jpeg", 0.6)
      ),
    ]);

    return { photoBlob, thumbnailBlob };
  }, []);

  return { ...state, start, stop, capture };
};
